import { useEffect, useState } from 'react';
import {
  fetchCostCheckMonthly,
  type CostCheckHeadingKey,
  type CostCheckMonth,
} from '../../services/profitabilityTruthClient';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym: string) => `${SHORT_MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym} ${ym.slice(0, 4)}`;
const gbp = (n: number | null | undefined) =>
  typeof n === 'number' ? `£${Math.round(n).toLocaleString('en-GB')}` : 'Unknown';

const HEADING_LABEL: Record<CostCheckHeadingKey, string> = {
  fuel: 'Fuel',
  driver_pay: 'Driver pay',
  vehicle: 'Vehicle costs',
  network: 'Network charge',
};

/** A gap this large (share of the Xero figure) is highlighted. */
const BIG_GAP_PCT = 20;

const isBigGap = (pct: number | null) => pct != null && Math.abs(pct) >= BIG_GAP_PCT;

function gapText(gap: number | null, pct: number | null): string {
  if (gap == null) return 'Unknown';
  const side = gap > 0 ? 'more in Xero' : gap < 0 ? 'less in Xero' : 'same';
  return `${gbp(Math.abs(gap))} ${side}${pct != null ? ` (${Math.abs(pct)}%)` : ''}`;
}

/**
 * The Profit page's costs for each ended month beside the Xero P&L
 * cost-of-sales lines, with the gap (plan: xero_monthly_rates_plan.md, step
 * 5a). Show only: no profit figure changes. Big gaps lead each month.
 */
export default function CostCheckMonthsTable() {
  const [months, setMonths] = useState<CostCheckMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchCostCheckMonthly()
      .then((r) => { if (live) setMonths(r.months); })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, []);

  return (
    <div>
      <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">
        Our costs against Xero, by month (for review)
      </h4>
      <p className="text-xs text-muted-foreground mb-2">
        The Profit page&apos;s costs for the month beside the Xero P&amp;L cost of sales lines. Nothing here changes a
        profit figure. Xero covers the whole business; the Profit page covers only the vehicles it works out profit for,
        so some gap is expected. Which Xero lines count under each heading is our suggestion until finance confirms it.
        Gaps of {BIG_GAP_PCT}% or more are highlighted.
      </p>
      {error ? (
        <p className="text-xs text-warning">Could not load the cost check: {error}</p>
      ) : months === null ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : months.length === 0 ? (
        <p className="text-xs text-muted-foreground">No ended months to compare yet.</p>
      ) : (
        <div className="space-y-4">
          {months.map((m) => (
            <CostCheckMonthBlock key={m.month} month={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function CostCheckMonthBlock({ month: m }: { month: CostCheckMonth }) {
  const big = m.headings.filter((h) => isBigGap(h.gap_pct));
  // Exceptions first: the big gaps lead, largest first; the rest keep their order.
  const rows = [
    ...big.sort((a, b) => Math.abs(b.gap_pct ?? 0) - Math.abs(a.gap_pct ?? 0)),
    ...m.headings.filter((h) => !isBigGap(h.gap_pct)),
  ];
  const partial = m.coverage.days_with_profit < m.coverage.days_in_month;

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
        <span className="text-xs font-semibold text-foreground">{monthLabel(m.month)}</span>
        <span className="text-xs text-muted-foreground">{m.status === 'final' ? 'Final' : 'Provisional'}</span>
        {big.length > 0 ? (
          <span className="text-xs text-warning">
            {big.length === 1 ? '1 big gap' : `${big.length} big gaps`}: {big.map((h) => HEADING_LABEL[h.heading]).join(', ')}
          </span>
        ) : null}
      </div>
      {m.missing ? (
        <p className="text-xs text-warning mb-1">{m.missing}</p>
      ) : partial ? (
        <p className="text-xs text-warning mb-1">
          Profit was worked out for {m.coverage.days_with_profit} of {m.coverage.days_in_month} days, so the Profit
          page&apos;s figures cover part of the month only.
        </p>
      ) : null}
      {!m.missing && m.coverage.no_gps_vehicle_days > 0 ? (
        <p className="text-xs text-warning mb-1">
          {m.coverage.no_gps_vehicle_days} vehicle-day{m.coverage.no_gps_vehicle_days === 1 ? '' : 's'} had no Samsara
          mileage data, so their mileage-based costs (tyres, repairs, fuel worked out from miles, part of driver pay) are
          missing from the Profit page figures.
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-144">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th className="py-2 px-2 font-medium border-b border-border">Heading</th>
              <th className="py-2 px-2 font-medium border-b border-border text-right">Profit page</th>
              <th className="py-2 px-2 font-medium border-b border-border text-right">Xero</th>
              <th className="py-2 px-2 font-medium border-b border-border text-right">Gap</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.heading} className="border-b border-border/50 text-foreground">
                <td className="py-1.5 px-2">{HEADING_LABEL[h.heading]}</td>
                <td className="py-1.5 px-2 text-right">{gbp(h.modelled_gbp)}</td>
                <td
                  className="py-1.5 px-2 text-right"
                  title={h.xero_lines.length > 0 ? h.xero_lines.map((l) => `${l.label}: ${gbp(l.amount)}`).join('\n') : 'No Xero line matched'}
                >
                  {gbp(h.xero_gbp)}
                </td>
                <td className={`py-1.5 px-2 text-right ${isBigGap(h.gap_pct) ? 'text-warning font-medium' : ''}`}>
                  {gapText(h.gap_gbp, h.gap_pct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {m.unmapped_cost_of_sales.length > 0 || m.operating_expenses_gbp != null ? (
        <details className="mt-1">
          <summary className="text-xs text-muted-foreground cursor-pointer">Xero costs not compared</summary>
          <ul className="text-xs text-muted-foreground mt-1 space-y-0.5 pl-4">
            {m.unmapped_cost_of_sales.map((l) => (
              <li key={l.label}>
                {l.label}: {gbp(l.amount)}
              </li>
            ))}
            {m.operating_expenses_gbp != null ? (
              <li>Overheads (operating expenses), not modelled on the Profit page: {gbp(m.operating_expenses_gbp)}</li>
            ) : null}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
